import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Group,
  Modal,
  Progress,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useApp } from "../../context/AppContext";
import Logo from "../common/Logo/Logo";
import PersonalizeForm from "./wizard/PersonalizeForm";
import ValueRewardsForm from "./wizard/ValueRewardsForm";
import ActionRewardsForm from "./wizard/ActionRewardsForm";
import {
  PersonalizeSummaryCard,
  ValueRewardsSummaryCard,
  ActionRewardsSummaryCard,
} from "./wizard/SummarySections";
import {
  LoyaltyWizardDraft,
  defaultWizardDraft,
  isActionRewardsValid,
  redeemRewardName,
  isValueRewardsValid,
  presetShowsProducts,
  presetShowsServices,
  toNumber,
  EARN_PER_AMOUNT,
  ROLLING_EXPIRATION_DAYS,
} from "./wizard/types";
import { LoyaltyEarnRuleType } from "../../types/loyalty";

type WizardStep = 1 | 2 | 3 | 4;
const TOTAL_STEPS = 4;

/** Tracks which Publish writes already succeeded so a retry after a partial
 *  failure never duplicates them (loyalty FE1 §2.6). */
interface PublishProgress {
  settingsDone: boolean;
  spendRulesDone: Partial<Record<LoyaltyEarnRuleType, boolean>>;
  rebookingDone: boolean;
  nthVisitDone: boolean;
  /** First appointment publishes as a capped nth_visit (interval_count 1). */
  firstVisitDone: boolean;
  /** Single redemption reward POST (§2.6 step 4). */
  rewardDone: boolean;
}

const emptyPublishProgress = (): PublishProgress => ({
  settingsDone: false,
  spendRulesDone: {},
  rebookingDone: false,
  nthVisitDone: false,
  firstVisitDone: false,
  rewardDone: false,
});

interface LoyaltyOnboardingWizardProps {
  opened: boolean;
  businessLocationId: string;
  /** Cancel / X — discards all client-side wizard state. */
  onClose: () => void;
  /** Success-screen Continue — caller refreshes settings and shows the tabs. */
  onPublished: () => void;
}

/**
 * One-time loyalty program onboarding wizard (loyalty FE1 §2). Modal with a
 * progress bar and Back/Next/Cancel. All state is client-side; nothing is
 * written until Publish (§2.6). After Publish, editing happens only via the
 * Settings tab — the wizard is not re-entered.
 */
export default function LoyaltyOnboardingWizard({
  opened,
  businessLocationId,
  onClose,
  onPublished,
}: LoyaltyOnboardingWizardProps) {
  const {
    updateLoyaltySettings,
    createLoyaltyEarnRule,
    createLoyaltyReward,
    getLoyaltyRewards,
    updateLoyaltyReward,
  } = useApp();

  const [step, setStep] = useState<WizardStep>(1);
  const [draft, setDraft] = useState<LoyaltyWizardDraft>(defaultWizardDraft);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [published, setPublished] = useState(false);
  const [publishProgress, setPublishProgress] =
    useState<PublishProgress>(emptyPublishProgress);
  // Bumped on every reset; a publish() that finishes after a reset is stale
  // and must not write state (it would overwrite the reset, e.g. flipping
  // published=true after the user cancelled).
  const publishGenerationRef = useRef(0);

  const resetAndClose = useCallback(() => {
    publishGenerationRef.current += 1;
    setStep(1);
    setDraft(defaultWizardDraft());
    setIsPublishing(false);
    setPublishError(null);
    setPublished(false);
    setPublishProgress(emptyPublishProgress());
    onClose();
  }, [onClose]);

  const handleContinue = useCallback(() => {
    publishGenerationRef.current += 1;
    setStep(1);
    setDraft(defaultWizardDraft());
    setIsPublishing(false);
    setPublishError(null);
    setPublished(false);
    setPublishProgress(emptyPublishProgress());
    onPublished();
  }, [onPublished]);

  const stepValid = useMemo(() => {
    if (step === 2) {
      return isValueRewardsValid(draft.valueRewards, draft.personalize.preset);
    }
    if (step === 3) {
      return isActionRewardsValid(draft.actionRewards);
    }
    return true;
  }, [step, draft]);

  /** §2.6 — writes in order; resumable so a retry never duplicates a
   *  succeeded write. Throws with a user-facing message on first failure. */
  const publish = useCallback(async () => {
    const generation = publishGenerationRef.current;
    setIsPublishing(true);
    setPublishError(null);
    // Work on a local copy so mid-flight successes survive a later failure.
    const progress: PublishProgress = {
      settingsDone: publishProgress.settingsDone,
      spendRulesDone: { ...publishProgress.spendRulesDone },
      rebookingDone: publishProgress.rebookingDone,
      nthVisitDone: publishProgress.nthVisitDone,
      firstVisitDone: publishProgress.firstVisitDone,
      rewardDone: publishProgress.rewardDone,
    };
    const { personalize, valueRewards, actionRewards } = draft;
    try {
      // 1. Settings
      if (!progress.settingsDone) {
        await updateLoyaltySettings(businessLocationId, {
          enabled: true,
          preset: personalize.preset,
          deduct_redemption_from_commission: personalize.deductFromCommission,
          points_expiration_policy: personalize.expirationPolicy,
          points_expiration_days:
            personalize.expirationPolicy === "rolling"
              ? toNumber(personalize.expirationDays, ROLLING_EXPIRATION_DAYS)
              : null,
        });
        progress.settingsDone = true;
      }

      // 2. Spend earn rule(s) per preset, split per-type rates
      const spendRules: { type: LoyaltyEarnRuleType; points: number }[] = [];
      if (presetShowsServices(personalize.preset)) {
        spendRules.push({
          type: "service_spend",
          points: toNumber(valueRewards.servicesRate),
        });
      }
      if (presetShowsProducts(personalize.preset)) {
        spendRules.push({
          type: "retail_spend",
          points: toNumber(valueRewards.productsRate),
        });
      }
      for (const { type, points } of spendRules) {
        if (!progress.spendRulesDone[type]) {
          await createLoyaltyEarnRule(businessLocationId, {
            rule_type: type,
            points,
            per_amount: EARN_PER_AMOUNT,
          });
          progress.spendRulesDone[type] = true;
        }
      }

      // 3. Action rules
      // Rebooking is always unlimited.
      if (actionRewards.rebookingEnabled && !progress.rebookingDone) {
        await createLoyaltyEarnRule(businessLocationId, {
          rule_type: "rebooking",
          points: toNumber(actionRewards.rebookingPoints),
        });
        progress.rebookingDone = true;
      }
      if (actionRewards.visitsEnabled && !progress.nthVisitDone) {
        await createLoyaltyEarnRule(businessLocationId, {
          rule_type: "nth_visit",
          points: toNumber(actionRewards.visitsPoints),
          interval_count: toNumber(actionRewards.visitsInterval, 1),
        });
        progress.nthVisitDone = true;
      }
      // First appointment: capped nth_visit (once per client, on first visit).
      if (actionRewards.firstAppointmentEnabled && !progress.firstVisitDone) {
        await createLoyaltyEarnRule(businessLocationId, {
          rule_type: "nth_visit",
          points: toNumber(actionRewards.firstAppointmentPoints),
          interval_count: 1,
          max_awards_per_client: 1,
        });
        progress.firstVisitDone = true;
      }

      // 4. Single redemption reward; resumable via `rewardDone`. Idempotent
      //    against a rerun of the wizard on a location that already has a
      //    reward (from SettingsTab or a prior wizard run): fresh GET, reuse
      //    the first active row (PATCH values if drifted), deactivate any
      //    extras, only POST when none exist. Mirrors SettingsTab's
      //    saveValueRewards dedupe so the two entry points can't leak
      //    duplicate rows into the checkout picker.
      if (!progress.rewardDone) {
        const row = valueRewards.redemptionIncrement;
        const pointsCost = toNumber(row.pointsCost);
        const cashValue = toNumber(row.cashValue);
        const body = {
          name: redeemRewardName(pointsCost, cashValue),
          points_cost: pointsCost,
          cash_value: cashValue,
        };
        const freshRewards = await getLoyaltyRewards(businessLocationId);
        const activeRewards = freshRewards.filter((r) => r.is_active !== false);
        const target = activeRewards[0];
        if (!target) {
          await createLoyaltyReward(businessLocationId, body);
        } else {
          const currentCash = toNumber(target.cash_value);
          const currentPoints = toNumber(target.points_cost);
          if (currentCash !== cashValue || currentPoints !== pointsCost) {
            await updateLoyaltyReward(businessLocationId, target.uuid, body);
          }
          for (const extra of activeRewards) {
            if (extra.uuid !== target.uuid) {
              await updateLoyaltyReward(businessLocationId, extra.uuid, {
                is_active: false,
              });
            }
          }
        }
        progress.rewardDone = true;
      }

      if (publishGenerationRef.current === generation) {
        setPublished(true);
      }
    } catch (error: any) {
      console.error("Error publishing loyalty program:", error);
      if (publishGenerationRef.current === generation) {
        const serverMessage =
          error?.status >= 400 && error?.status < 500 && error?.message
            ? error.message
            : null;
        setPublishError(
          serverMessage ||
            "Something went wrong publishing your program. Nothing was lost — review the setup and try again.",
        );
      }
    } finally {
      if (publishGenerationRef.current === generation) {
        setPublishProgress(progress);
        setIsPublishing(false);
      }
    }
  }, [
    draft,
    businessLocationId,
    publishProgress,
    updateLoyaltySettings,
    createLoyaltyEarnRule,
    createLoyaltyReward,
    getLoyaltyRewards,
    updateLoyaltyReward,
  ]);

  const goBack = () => setStep((s) => Math.max(1, s - 1) as WizardStep);
  const goNext = () =>
    setStep((s) => Math.min(TOTAL_STEPS, s + 1) as WizardStep);

  return (
    <Modal
      opened={opened}
      onClose={published ? handleContinue : resetAndClose}
      title={<Logo variant="horizontal" size="md" />}
      fullScreen
      closeOnClickOutside={false}
      closeOnEscape={!isPublishing}
      withCloseButton={!isPublishing}
    >
      {published ? (
        <Stack align="center" justify="center" gap="md" mih="50dvh" p="xl">
          <Title order={2} ta="center">
            Your loyalty program is live!
          </Title>
          <Text size="sm" c="dimmed" ta="center">
            Clients will start earning points automatically. Visit Settings to
            make changes.
          </Text>
          <Button color="var(--brand-teal)" onClick={handleContinue}>
            Continue
          </Button>
        </Stack>
      ) : (
        <Stack gap="lg" p="md" maw="64rem" mx="auto" w="100%">
          <Progress
            value={(step / TOTAL_STEPS) * 100}
            color="var(--brand-teal)"
            size="lg"
            radius="xl"
            aria-label={`Step ${step} of ${TOTAL_STEPS}`}
          />

          {step === 1 && (
            <PersonalizeForm
              value={draft.personalize}
              servicesRate={draft.valueRewards.servicesRate}
              productsRate={draft.valueRewards.productsRate}
              onChange={(personalize) =>
                setDraft((prev) => ({ ...prev, personalize }))
              }
            />
          )}
          {step === 2 && (
            <ValueRewardsForm
              value={draft.valueRewards}
              preset={draft.personalize.preset}
              onChange={(valueRewards) =>
                setDraft((prev) => ({ ...prev, valueRewards }))
              }
            />
          )}
          {step === 3 && (
            <ActionRewardsForm
              value={draft.actionRewards}
              onChange={(actionRewards) =>
                setDraft((prev) => ({ ...prev, actionRewards }))
              }
            />
          )}
          {step === 4 && (
            <Stack gap="lg">
              <Stack gap={4}>
                <Title order={2}>Review &amp; Publish</Title>
                <Text size="sm" c="dimmed">
                  Check that everything looks right. You can always make changes
                  later.
                </Text>
              </Stack>
              <PersonalizeSummaryCard
                value={draft.personalize}
                servicesRate={draft.valueRewards.servicesRate}
                productsRate={draft.valueRewards.productsRate}
                onEdit={() => setStep(1)}
              />
              <ValueRewardsSummaryCard
                value={draft.valueRewards}
                preset={draft.personalize.preset}
                onEdit={() => setStep(2)}
              />
              <ActionRewardsSummaryCard
                value={draft.actionRewards}
                onEdit={() => setStep(3)}
              />
              {publishError && (
                <Alert
                  color="red"
                  variant="light"
                  icon={<IconAlertCircle size={18} />}
                  title="Publish failed"
                >
                  {publishError}
                </Alert>
              )}
            </Stack>
          )}

          <Group justify="flex-end" gap="sm">
            {step === 1 ? (
              <Button
                variant="default"
                onClick={resetAndClose}
                disabled={isPublishing}
              >
                Cancel
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={goBack}
                disabled={isPublishing}
              >
                Back
              </Button>
            )}
            {step < TOTAL_STEPS ? (
              <Button
                color="var(--brand-teal)"
                onClick={goNext}
                disabled={!stepValid}
              >
                Next
              </Button>
            ) : (
              <Button
                color="var(--brand-teal)"
                onClick={publish}
                loading={isPublishing}
              >
                {publishError ? "Retry Publish" : "Publish"}
              </Button>
            )}
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
